import { useCallback, useMemo, useState } from "react";
import { Button } from "@heroui/react";
import { showcaseFromConfig } from "../../hooks/home-story-showcase";
import { useHomeBoot } from "../../hooks/useHomeBoot";
import { useHomeConfig } from "../../hooks/useHomeConfig";
import type { HomeStoryStatus } from "../../hooks/useHomeStoryScene";
import { HomeCommunity } from "./components/HomeCommunity";
import { HomeFeatured } from "./components/HomeFeatured";
import { SiteFooter } from "../../components/site-footer/SiteFooter";
import { SiteHeader } from "../../components/site-header/SiteHeader";
import { HomeLoader } from "./components/HomeLoader";
import { HomeMaterials } from "./components/HomeMaterials";
import { HomeMaterialCharacters } from "./components/HomeMaterialCharacters";
import { HomeStory } from "./components/HomeStory";
import { useClientSession } from "../../hooks/useClientSession";
import { useHomeFeed } from "./useHomeFeed";
import "./home-shell.css";
import "./home-story.css";
import "./home-story-type.css";
import "./home-characters.css";
import "./home-character-scene.css";
import "./home-skip.css";
import "./home-featured.css";
import "./home-materials.css";
import "./home-community.css";
import "./home-motion.css";
import "./home-loader.css";

export default function HomePage() {
  const clientSession = useClientSession();
  const feed = useHomeFeed();
  const home = useHomeConfig();
  const signedIn = Boolean(clientSession.session.id);
  const [sceneStatus, setSceneStatus] = useState<HomeStoryStatus>("loading");
  const onSceneStatus = useCallback((status: HomeStoryStatus) => setSceneStatus(status), []);
  const showcase = useMemo(() => (home.config ? showcaseFromConfig(home.config) : null), [home.config]);
  const showStory = home.config?.story.enabled ?? !home.loading;
  const showCharacters = Boolean(home.config?.characters.enabled && home.config.characters.items.length);
  const boot = useHomeBoot({
    sceneSettled: !showStory || sceneStatus !== "loading",
    dataSettled: feed.materialsReady && !home.loading,
  });

  return (
    <div className={`home-page${showStory ? " has-story" : ""}${boot.finished ? " is-booted" : ""}`}>
      {boot.active ? <HomeLoader progress={boot.progress} finished={boot.finished} /> : null}
      <SiteHeader session={clientSession.session} loading={clientSession.loading} onLogout={clientSession.logout} />
      <main>
        {showStory && (
          <HomeStory
            signedIn={signedIn}
            loadingSession={clientSession.loading}
            showcase={showcase}
            onStatusChange={onSceneStatus}
            nextSection={showCharacters ? "home-characters" : "featured"}
          />
        )}
        {home.loading && !home.config && (
          <p className="home-config-status" role="status">
            正在载入首页内容…
          </p>
        )}
        {home.error && (
          <div className="home-config-status" role="alert">
            {home.error}
            <Button size="sm" variant="tertiary" isPending={home.loading} onPress={home.retry}>
              重新加载
            </Button>
          </div>
        )}
        {showCharacters && home.config && <HomeMaterialCharacters key={home.config.revision} config={home.config.characters} />}
        <HomeFeatured notes={feed.featured} loading={feed.loading} error={feed.error} onRetry={feed.reload} />
        <HomeMaterials
          materials={feed.materials}
          types={feed.materialTypes}
          loading={feed.loading}
          error={feed.error}
          onRetry={feed.reload}
        />
        <HomeCommunity notes={feed.latest} topics={feed.topics} loading={feed.loading} />
      </main>
      <SiteFooter />
    </div>
  );
}
